import {
  Overwrite,
  PartiallyPartial,
  PartiallyRequired
} from "@/helpers/type-helper";
import { AxiosInstance, AxiosRequestConfig } from "axios";

export enum HttpStatusCode {
  Success = 200,
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  LengthRequired = 411,
  UnprocessableEntity = 422,
  InternalServerError = 500
}

export enum BME_PART {
  BEGINNING = 1,
  MIDDLE = 2,
  END = 3,
  UNKNOWN = 0
}

export enum EventCategory {
  Empty = "",
  Event = "event",
  EntrySheet = "entry_sheet",
  GroupDiscussion = "group_discussion",
  JobSelection = "job_selection",
  ObInterview = "ob_interview",
  Interview = "interview",
  Recruiter = "recruiter",
  PaperExamination = "paper_examination",
  Prehire = "prehire",
  WebExamination = "web_examination"
}

export enum EventPurpose {
  Intern = "intern",
  Recruitment = "recruitment",
  Seminar = "seminar"
}

export enum PurposeNames {
  Intern = "インターン",
  Recruitment = "本選考",
  Seminar = "説明会"
}

export const EventPurposeNames = {
  [EventPurpose.Intern]: PurposeNames.Intern,
  [EventPurpose.Recruitment]: PurposeNames.Recruitment,
  [EventPurpose.Seminar]: PurposeNames.Seminar
};

export enum InHouseOtherCompetitors {
  InHouse = "自社",
  Competitors = "他社"
}

export type DATE_TYPE = "BME" | "DATE_RANGE";

export type ORDER_TYPE = "ASC" | "DESC";

export type NOTIFICATION_TYPE = "report" | "event" | "experience" | "scout";

export const RecruitmentEventTypes = [
  { label: "イベント", value: EventCategory.Event },
  { label: "OB訪問", value: EventCategory.ObInterview },
  { label: "リクルーター面談", value: EventCategory.Recruiter },
  { label: "エントリーシート", value: EventCategory.EntrySheet },
  { label: "WEBテスト", value: EventCategory.WebExamination },
  { label: "筆記試験", value: EventCategory.PaperExamination },
  { label: "グループディスカッション", value: EventCategory.GroupDiscussion },
  { label: "面接", value: EventCategory.Interview },
  { label: "ジョブ選考", value: EventCategory.JobSelection },
  { label: "内定", value: EventCategory.Prehire }
];

// TODO: 自社イベントと他社イベントのイベント種別名が全く同じであれば、RecruitmentEventTypesと統合しても良い
export const EventCategoryNames = {
  [EventCategory.Event]: "イベント",
  [EventCategory.EntrySheet]: "エントリーシート",
  [EventCategory.GroupDiscussion]: "グループディスカッション（GD）",
  [EventCategory.JobSelection]: "ジョブ選考",
  [EventCategory.ObInterview]: "OB訪問",
  [EventCategory.Interview]: "面接",
  [EventCategory.Recruiter]: "リクルーター面談",
  [EventCategory.PaperExamination]: "筆記試験",
  [EventCategory.Prehire]: "内定",
  [EventCategory.WebExamination]: "WEBテスト"
};

export enum CompetitorsQuerySort {
  Likes = "LIKES",
  Reviews = "REVIEWS",
  LikesDup = "LIKES_DUP",
  Prehire = "PREHIRE"
}

export enum NotificationFilterCategory {
  ALL = "all",
  EXPERIENCE = "experience",
  PARTNER_NOTIFICATION_NEW = "partner_notification_new",
  PARTNER_NOTIFICATION_CHANGE = "partner_notification_change",
  PARTNER_NOTIFICATION_CONFIRM = "partner_notification_confirm"
}

export class BaseError<T extends Error> extends Error {
  constructor(error: T) {
    super(error.message);
  }
}

export class UnauthorizedError extends BaseError<ApiResponse.ApiError> {
  response: ApiResponse.ErrorResponse;
  constructor(error: ApiResponse.ApiError) {
    super(error);
    this.response = error.response;
  }
}

export class BadRequestError extends BaseError<ApiResponse.ApiError> {
  response: ApiResponse.ErrorResponse;
  constructor(error: ApiResponse.ApiError) {
    super(error);
    this.response = error.response;
  }
}

export class ForbiddenError extends BaseError<ApiResponse.ApiError> {
  response: ApiResponse.ErrorResponse;
  constructor(error: ApiResponse.ApiError) {
    super(error);
    this.response = error.response;
  }
}

export class NotFoundError extends BaseError<ApiResponse.ApiError> {
  response: ApiResponse.ErrorResponse;
  constructor(error: ApiResponse.ApiError) {
    super(error);
    this.response = error.response;
  }
}

export class UnprocessableEntityError extends BaseError<ApiResponse.ApiError> {
  response: ApiResponse.ErrorResponse;
  constructor(error: ApiResponse.ApiError) {
    super(error);
    this.response = error.response;
  }
}

export class InternalServerError extends BaseError<ApiResponse.ApiError> {
  response: ApiResponse.ErrorResponse;
  constructor(error: ApiResponse.ApiError) {
    super(error);
    this.response = error.response;
  }
}

export class NetworkError extends BaseError<Error> {}

export interface ExAxiosInstance extends AxiosInstance {
  callbacks: {
    apiErrors: Array<(error: Error) => void>;
  };
}

export type ReportType = "MONTHLY" | "WEEKLY";

export enum AwardBadge {
  GOLD = "gold",
  SILVER = "silver",
  BRONZE = "bronze",
  VENTURE = "venture"
}

export enum CompanyDetailReviewCategory {
  SEMINAR = "seminar",
  INTERN = "intern",
  RECRUITMENT = "recruitment"
  // TODO: ENTRY_SHEET の項目は今後対応
  // ENTRY_SHEET = "entrySheet"
}

export enum RatingsPoint {
  "1s" = 1.0,
  "2s" = 2.0,
  "3s" = 3.0,
  "4s" = 4.0
}

export interface CompanyDetailRating {
  point: RatingsPoint;
  count: number;
}

export interface UserReports {
  category: CompanyDetailReviewCategory;
  count: number;
}

export interface NotificationSetting extends ApiResponse.NotificationSetting {}

export type JobEventApprovalStatus =
  | "draft"
  | "pending"
  | "approved"
  | "waiting_for_publish"
  | "rejected";

export type JobEventPublicStatus =
  | "private"
  | "coming_soon"
  | "public"
  | "stopped";

export type JobEventsSort = "title" | "updated_at";

export type Order = "asc" | "desc";

export type SponsoredEventsSort = "title" | "updated_at";

export type PartnerEntryUserSelectionStatus =
  | "entried" // 選考中
  | "unofficial_offered" // 内定
  | "approved" // 内定承諾
  | "declined" // 内定辞退
  | "rejected"; // 除外

export type ChangableLotteryEventStatus =
  | "elected"
  | "defeated"
  | "attended"
  | "absented";

export type ChangableUnlotteryEventStatus = "attended" | "absented";

export type PartnerEntryUserEventStatus =
  | "invited"
  | "pre_entried"
  | "entried"
  | "elected"
  | "defeated"
  | "absented"
  | "attended"
  | "canceled";

export type PartnerEntryUserInternshipStatus =
  | "completed"
  | "canceled"
  | "planing";

export type PartnerEntryUserUnofficialOfferStatus =
  | "accept"
  | "decline"
  | "hold";

export type PartnerEntryUserFilterData = {
  searchText: string;
  like: boolean;
  unlottery: boolean;
  universities: string[];
  graduateYears: string[];
  grades: string[];
  selectionStatus: PartnerEntryUserSelectionStatus[];
  eventStatus: PartnerEntryUserEventStatus[];
  entryRoute: number[];
  schedules: number[];
  entryDate: { from: string; to: string };
};

export type PartnerEntryUserFilterName =
  | "like"
  | "unlottery"
  | "universities"
  | "graduateYears"
  | "grades"
  | "selectionStatus"
  | "eventStatus"
  | "entryRoute"
  | "schedules"
  | "entryDate";

export type PartnerEntryUserFilterType =
  | "checkbox"
  | "multiSelectbox"
  | "dateRange";

export type OfficialPublicStatus =
  | "private"
  | "coming_soon"
  | "suspend"
  | "public"
  | "ended";

export type OfficialApprovalStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected";

export type OfficialContentType =
  | "content_type_1"
  | "content_type_3"
  | "content_type_7";

export interface PartnerEntryUserFilterCondition {
  label: string;
  checkbox_label?: string;
  name: PartnerEntryUserFilterName;
  type: PartnerEntryUserFilterType;
}

export interface JobEventDescription {
  id: number;
  label: string;
  content: string;
  position: number;
  _destroy?: true;
}

export interface JobEventResume {
  id: number;
  title: string;
  description?: string;
  position: number;
  min_length: number;
  _destroy?: true;
}

export interface JobEventSchedule {
  id: number | string;
  title: string;
  time_limit_at: string;
  start_at?: string;
  end_at?: string;
  place?: string;
  limited_place?: string;
  live_url?: string;
  prefecture?: string;
  admission_limit?: number;
  entry_stopped: boolean;
  canceled: boolean;
  schedule_id?: number;
  mail_template: {
    id?: number;
  } & JobEventMailType;
  published_at?: string;
  _destroy?: true;
}

export type EditingJobEventScheduleType = "online" | "offline";

export interface EditingJobEventSchedule extends JobEventSchedule {
  schedule_type?: EditingJobEventScheduleType;
}

export interface JobEventDraftSchedule {
  id: number;
  title: string | null;
  time_limit_at: string | null;
  draft_event_id: number | null;
  schedule_id: number | null;
  start_at: string | null;
  end_at: string | null;
  place: string | null;
  limited_place: string | null;
  live_url: string | null;
  prefecture: string | null;
  detailed_address: string | null;
  admission_limit: number | null;
  entry_stopped: boolean | null;
  canceled: boolean | null;
  entry_mail_template: object | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobEventMailType {
  pre_entry: string;
  entry: string;
  pass?: string;
  elected?: string;
  defeated?: string;
  request_attend_response?: string;
  reminder?: string;
  thanks?: string;
  user_canceled?: string;
  schedule_canceled?: string;
}

export interface JobEventMailUpdatedAtType {
  pre_entry_updated_at: string;
  entry_updated_at: string;
  pass_updated_at: string;
  elected_updated_at: string;
  defeated_updated_at: string;
  request_attend_response_updated_at: string;
  reminder_updated_at: string;
  thanks_updated_at: string;
  user_canceled_updated_at: string;
  schedule_canceled_updated_at: string;
}

export type JobEventMailNotification = Record<
  keyof Pick<JobEventMailType, "reminder" | "thanks">,
  boolean
>;

export type JobEventScheduleMailKey = Exclude<
  keyof JobEventSchedule["mail_template"],
  "id"
>;

export interface JobEventComment {
  reject_reason: string;
  reject_at: string;
}

export type JobEventReceptionType = "internal" | "external";

export type JobEventCategory = "seminar" | "internship" | "entry";

export type IwebType = "intern" | "entry";

export type EventEntryUserEventStatus =
  | "invited"
  | "pre_entried"
  | "entried"
  | "elected"
  | "defeated"
  | "absented"
  | "attended"
  | "canceled";

export interface EventEntryUserFilterCondition {
  label: string;
  checkbox_label?: string;
  name: EventEntryUserFilterName;
  type: EventEntryUserFilterType;
}

export type EventEntryUserFilterName =
  | "starred"
  | "unlottery"
  | "universities"
  | "graduateYears"
  | "grades"
  | "eventStatus"
  | "schedules"
  | "entryDate";

export type EventEntryUserFilterType =
  | "checkbox"
  | "multiSelectbox"
  | "dateRange";

export type EventEntryUserFilterData = {
  departmentSearchText: string;
  starred: boolean;
  unlottery: boolean;
  universities: string[];
  graduateYears: string[];
  grades: string[];
  selectionStatus: PartnerEntryUserSelectionStatus[];
  eventStatus: EventEntryUserEventStatus[];
  schedules: number[];
  entryDate: { from: string; to: string };
};

export type AnnouncementType = "news" | "terms";
export type AnnouncementMethod = "news" | "popup";
export type TermsType =
  | "recruitment_plan"
  | "job_posting"
  | "scout"
  | "outsourcing"
  | "recruitment_management"
  | "privacy_policy"
  | "personal_information";

export namespace ApiRequest {
  export interface SignIn {
    email: string;
    password: string;
  }

  export interface SignInToken {
    partner_user: {
      oc_token: string;
    };
  }

  export interface PasswordReissue {
    email: string;
  }

  export interface PasswordReset {
    reset_password_token: string;
    password: string;
    password_confirmation: string;
  }

  export interface GetFiles {
    file_name?: string;
    order: ORDER_TYPE;
    limit?: number;
    offset?: number;
  }

  export interface GetDownloadFile {
    file_id: number;
    file_name: string;
  }

  export interface RegisterOnboarding {
    min_recruitment_number?: number; // NOTE: recruitment_number_registered がtrueなら省略
    max_recruitment_number?: number; // NOTE: recruitment_number_registered がtrueなら省略
    university_ids: number[];
    company_ids: number[];
  }

  export interface CreatePlan {
    partnerId: number;
    name: string;
    graduateYear: number;
  }

  export interface DuplicatePlan {
    partnerId: number;
    planId: number;
    name: string;
    graduateYear: number;
  }

  export interface UpdatePlan {
    partnerId: number;
    planId: number;
    name: string;
  }

  export interface DeletePlan {
    partnerId: number;
    planId: number;
  }

  export interface CreateEvent {
    partner_id: number;
    plan_id: number;
    recruitmentEvent: {
      name: String;
      event_type: String;
      start_at: String;
      end_at: String;
      description: String;
      reference_url: String;
    };
  }

  export interface UpdateEvent {
    partner_id: number;
    plan_id: number;
    recruitment_event_id: number;
    recruitmentEvent: Application.RecruitmentEvent;
  }

  export interface DeleteEvent {
    partner_id: number;
    plan_id: number;
    recruitment_event_id: number;
  }

  export interface GetPlans {
    partnerId: number;
  }

  export interface GetPlan {
    partner_id: number;
    plan_id: number;
  }

  export interface GetCompanyList {
    partner_id: number;
    plan_id: number;
    company_name?: Application.CompanyName;
    limit?: number;
    offset?: number;
    business_category_id?: string;
    sort?: CompetitorsQuerySort;
  }

  export interface GetUniversities {
    name: string;
  }

  export interface GetUniversityList {
    graduateYear: number;
    universityName: string;
    universityCourse: string;
  }

  export interface GetCompetitorEmbedded {
    partnerId: number;
    planId: number;
    competitorId: number;
    companyId: number; //TODO: おそらく使われていない
  }

  export interface GetStudentEmbedded {
    partnerId: number;
    planId: number;
    studentId: number;
  }

  export interface CreateCompetitor {
    partnerId: number;
    planId: number;
    company: { id: number };
  }

  export interface UpdateCompetitor {
    partnerId: number;
    planId: number;
    competitorId: number;
    company: { sort_order_position: number };
  }

  export interface DeleteCompetitor {
    partnerId: number;
    planId: number;
    competitorId: number;
  }

  export interface DeleteUniversity {
    partnerId: number;
    planId: number;
    studentId: number;
  }

  export interface CreateUniversity {
    partnerId: number;
    planId: number;
    university: { name: string; course: string };
  }

  export interface UpdateUniversity {
    partnerId: number;
    planId: number;
    studentId: number;
    university: { name: string; course: string; sort_order_position?: number };
  }

  export interface GetStudentJobHuntingTrendCollection {
    partnerId: number;
    planId: number;
    universityId: number;
  }

  export interface GetEventExperiences {
    company_id: number;
    graduate_year: number;
    purpose: EventPurpose;
    category?: EventCategory;
    // TODO: BMEPeriodForCompetitorをApiResponseの外に出す
    start_at: ApiResponse.BMEPeriodForCompetitor;
    end_at: ApiResponse.BMEPeriodForCompetitor;
  }

  export interface GetEventExperience {
    eventExperienceId: number;
  }

  export interface GetEventDetail {
    eventDetailId: number;
  }

  export interface GetReports {
    planId: number;
  }

  export interface GetReport {
    planId: number;
    reportId: number;
    type: ReportType;
  }

  export interface GetNotifications {
    limit: number;
    offset: number;
    category?: NotificationFilterCategory;
  }

  export interface ReadNotification {
    notificationId: number;
  }

  export interface GetEventDetail {
    eventDetailId: number;
  }

  export type BUSINESS_CATEGORY_NAME =
    | ""
    | "コンサル・シンクタンク"
    | "金融"
    | "メーカー"
    | "商社"
    | "IT・通信"
    | "広告・マスコミ"
    | "人材・教育"
    | "インフラ・交通"
    | "不動産・建設"
    | "旅行・観光"
    | "ブライダル・美容・くらし"
    | "医療・福祉"
    | "小売・流通"
    | "公務員・団体職員"
    | "その他";

  export interface GetCompaniesExplorer {
    company_name?: Application.CompanyName;
    business_category_name?: BUSINESS_CATEGORY_NAME;
    rating_ranges?: number[];
    sort?: string;
    limit?: number;
    offset?: number;
  }

  export interface GetCompaniesExplorerRank {
    graduate_year?: number;
    business_category_name?: BUSINESS_CATEGORY_NAME;
    university_ids?: number[];
    limit?: number;
    offset?: number;
  }

  export interface GetCompanyDetail {
    company_id: string;
  }
  export interface GetBusinessCategoriesAverages {
    business_category_id: number;
  }

  export interface GetReviewGraph {
    company_id: string;
    category: CompanyDetailReviewCategory;
  }

  export interface GetFavoriteGraph {
    company_id: string;
  }

  export interface GetCompanyDetailRatings {
    company_id: string;
    category: CompanyDetailReviewCategory;
    rating_title: string;
  }

  export interface GetCompanyDetailReviews {
    company_id: string;
    category: CompanyDetailReviewCategory;
    points: RatingsPoint[];
    offset: number;
    limit: number;
  }

  export interface GetCompanyList {
    partner_id: number;
    plan_id: number;
    company_name?: Application.CompanyName;
    limit?: number;
    offset?: number;
    business_category_id?: string;
    sort?: CompetitorsQuerySort;
  }

  export interface GetTopFollowees {
    limit?: number;
    name?: string;
  }

  export interface GetEventExperiences {
    company_id: number;
    graduate_year: number;
    purpose: EventPurpose;
    category?: EventCategory;
    // TODO: BMEPeriodForCompetitorをApiResponseの外に出す
    start_at: ApiResponse.BMEPeriodForCompetitor;
    end_at: ApiResponse.BMEPeriodForCompetitor;
  }
  export type UpdateNotificationSetting = ApiResponse.NotificationSetting;

  export interface CreateClip {
    experience_id: number | null;
    event_detail_id: number | null;
  }

  export interface DeleteClip {
    clip_id: number;
  }

  export interface GetContentOffer {
    offerId: number;
  }

  export interface UpdateContentOffer extends GetContentOffer {
    mail_body: string;
  }

  export interface GetFollowCompanies {}
  export interface GetFollowUniversities {}

  export interface AddFollowCompanies {
    company_ids: number[];
  }

  export interface RemoveFollowCompanies {
    company_ids: number[];
  }
  export interface AddFollowUniversities {
    university_ids: number[];
  }

  export interface RemoveFollowUniversities {
    university_ids: number[];
  }

  export interface JobEvents {
    title?: string;
    archived?: boolean;
    unlottery?: boolean;
    approval_statuses?: JobEventApprovalStatus[];
    public_statuses?: JobEventPublicStatus[];
    graduate_years?: string[];
    sort: JobEventsSort;
    order: Order;
    limit?: number;
    offset?: number;
  }

  export interface GetJobEventParams {
    eventId: number;
  }
  export interface GetJobEventSchedulesParams {
    eventId: number;
  }
  export interface UpdateJobEventDraftEntryMailTemplates {
    draftScheduleId: number;
    entry_mail_template: { id?: number | undefined } & JobEventMailType;
  }
  export interface GetJobEventDraftEntryMailTemplates {
    draftScheduleId: number;
  }
  export interface GetJobEventEntryMailTemplates {
    scheduleId: number;
  }
  export interface UpdateJobEventEntryMailTemplates {
    scheduleId: number;
    entry_mail_template: { id?: number | undefined } & JobEventMailType;
  }
  export interface UpdateJobEventMailNotificationTypes {
    eventId: number;
    mail_notification_types: string[];
  }

  /**
   * 公開依頼に必要なJobEvent情報
   */
  export interface JobEvent {
    draft_event: Overwrite<
      ApiResponse.JobEvent["draft_event"],
      {
        event_description_items: PartiallyPartial<JobEventDescription, "id">[];
        schedules: PartiallyPartial<JobEventSchedule, "id">[];
        resume_questions: PartiallyPartial<JobEventResume, "id">[];
      }
    >;
    comments: JobEventComment[];
  }

  /**
   * 下書き保存に必要なJobEvent情報
   */
  export type DraftJobEvent = {
    draft_event: PartiallyRequired<
      Partial<JobEvent["draft_event"]>,
      | "event_description_items"
      | "university_names"
      | "mail_notifications"
      | "schedules"
      | "resume_questions"
    >;
  };

  /**
   * 編集中のJobEvent(直接リクエストはしない)
   */
  export interface EditingDraftJobEvent
    extends Overwrite<
      PartiallyRequired<
        Partial<ApiResponse.JobEvent["draft_event"]>,
        | "event_description_items"
        | "university_names"
        | "mail_notifications"
        | "resume_questions"
      >,
      { schedules: EditingJobEventSchedule[] }
    > {}

  export interface CancelJobEventSchedule {
    scheduleId: number;
    originalScheduleId: number;
  }
  export interface SponsoredEvents {
    title?: string;
    graduate_years?: string[];
    sort: SponsoredEventsSort;
    order: Order;
    limit?: number;
    offset?: number;
  }

  export interface SponsoredEventEntryCheckinsBase {
    event_ids: number[];
    starred?: boolean;
    universities: string[];
    graduate_years: string[];
    grades: string[];
    schedule_ids: number[];
    keyword: string;
    entry_date_from?: string;
    entry_date_to?: string;
    order: string;
  }

  export interface SponsoredEventEntryCheckins
    extends SponsoredEventEntryCheckinsBase {
    limit: number;
    offset: number;
  }

  export interface SponsoredEvent {
    eventId: number;
  }

  export interface EntryCheckinsGenerateCsv
    extends SponsoredEventEntryCheckinsBase {
    output_columns: string[];
  }

  export interface SponsoredEventEntryApplicantsBase {
    event_ids: number[];
    universities: string[];
    graduate_years: string[];
    grades: string[];
    schedule_ids: number[];
    keyword: string;
    order: string;
    entry_date_from?: string;
    entry_date_to?: string;
  }

  export interface SponsoredEventEntryApplicants
    extends SponsoredEventEntryApplicantsBase {
    limit: number;
    offset: number;
  }

  export interface SponsoredEventEntryApplicantsGenerateCsv
    extends SponsoredEventEntryApplicantsBase {
    output_columns: string[];
  }

  export interface PartnerEntryUsers {
    search_text?: string;
    starred?: boolean;
    unlottery?: boolean;
    universities?: string[];
    graduate_years?: string[];
    grades?: string[];
    selection_status?: string[];
    event_status?: string[];
    entry_route?: string[];
    events?: number[];
    schedules?: number[];
    partner_entry_user_ids?: number[];
    entry_date_start?: string;
    entry_date_end?: string;
    order: Order;
    limit?: number;
    offset?: number;
  }

  export interface VideoBundleUnlockingLogs {
    search_text?: string;
    universities?: string[];
    graduate_years?: string[];
    grades?: string[];
    video_bundle_titles?: string[];
    video_chapter_titles?: string[];
    entry_date_from?: string;
    entry_date_to?: string;
    order: Order;
    limit?: number;
    offset?: number;
  }

  export interface VideoBundleUnlockingLogsCsvDownloadRequest {
    columns: string[];
  }

  export interface GetApplicantUser {
    user_id: number;
  }

  export interface GetScheduleOptions {
    event_id: number;
    user_id: number;
  }

  export interface UpdateApplyingSchedule {
    previous_schedule_id: number;
    next_schedule_id: number;
    user_id: number;
  }

  export interface UpdatePartnerEntryUserSelectionStatus {
    partner_entry_user_id: number;
    selection_status: PartnerEntryUserSelectionStatus;
  }

  export interface UpdatePartnerEntryUserEntryStatus {
    entry_id: number;
    state: ChangableLotteryEventStatus | ChangableUnlotteryEventStatus;
  }

  export interface GetPartnerEntryUserMemos {
    partner_entry_user_id: number;
  }

  export interface CreatePartnerEntryUserMemos {
    partner_entry_user_id: number;
    content: string;
  }

  export interface UpdatePartnerEntryUserMemos {
    partner_entry_user_memo_id: number;
    partner_entry_user_id: number;
    content: string;
  }

  export interface GetApplicantUserEntries {
    user_id: number;
  }

  export interface GetApplicantUserScoutEntry {
    user_id: number;
  }

  export interface GetPartnerEntryUserResumePdf {
    partner_entry_user_id: number;
    inline: boolean;
  }

  export interface GetPartnerEntryUserResumeAnswers {
    entry_id: number;
  }

  export interface PartnerEntryUsersGenerateCsv extends PartnerEntryUsers {
    output_columns: string[];
  }

  export interface UpdatePartnerEntryUserStar {
    partner_entry_user_id: number;
  }

  export interface BulkUpdatePartnerEntryUserStatus {
    partner_entry_users: ApiResponse.PartnerEntryUser[];
    status: PartnerEntryUserEventStatus | undefined;
  }

  export interface GetCsvDownloadFile {
    columns: string[];
  }

  export interface GetManagedPartnerUser {
    partner_user_id: number;
  }

  export interface DeleteManagedPartnerUser {
    partner_user_id: number;
  }

  export interface UpdateManagedPartnerUser {
    partner_user_id: number;
    email: string;
    name: string;
    password: string;
    admin: boolean;
  }

  export interface CreateManagedPartnerUser {
    email: string;
    name: string;
    admin: boolean;
  }

  export type UpdateRecruitmentNotificationSetting = ApiResponse.RecruitmentNotificationSetting;

  export interface getContractInformation {
    contract_types?: string[];
  }

  export interface UpdateIntegrationSonarToken {
    sonar_token: string;
  }

  export interface UpdateScoutIwebIntegrations {
    contract_graduate_year_ids: number[];
    graduate_year: number;
    code: string;
    integration_type: string;
  }

  export interface UpdateEventIwebIntegrations {
    event_ids: number[];
    graduate_year: number;
    code: string;
    integration_type: string;
  }

  export interface UpdatePartnerUserPrivilege {
    id: number;
    privileges: string[];
  }

  export type UpdatePartnerUserPrivilegeList = UpdatePartnerUserPrivilege[];

  export interface UpdatePartnerUserPrivileges {
    partner_users: UpdatePartnerUserPrivilegeList;
  }

  export interface GetAnnouncements {
    limit?: number;
    announce_type?: AnnouncementType;
    announce_method?: AnnouncementMethod;
  }

  export interface UpdateSelectionStatus {
    partner_entry_user_ids: number[];
    selection_status: PartnerEntryUserSelectionStatus;
  }
  export interface GetOfficials {
    partner_user_id: number;
  }

  export interface GetOfficial {
    official_id: number;
  }

  export interface OfficialData {
    id?: number;
    label: string;
    text: string;
    sort_order: number;
  }

  export interface OfficialContent {
    id?: number;
    content_type: OfficialContentType;
    title: string;
    description1: string;
    image1: string;
    movie1: string;
    position: number;
  }

  export interface CreateOfficial {
    official_description: string;
    official_image: string;
    top_head_image: string;
    contract_id: number;
    official_data: OfficialData[];
    official_contents?: OfficialContent[];
  }

  export interface UpdateOfficial {
    id: number;
    official_description: string;
    official_image: string;
    top_head_image: string;
    contract_id: number;
    official_data: OfficialData[];
    official_contents?: OfficialContent[];
  }

  export interface ConfirmOfficial {
    official_id: number;
    start_at: string;
    comment?: string;
  }

  export interface UnconfirmOfficial {
    official_id: number;
  }

  export interface EventEntryUsers {
    eventId: string;
    search_text?: string;
    starred?: boolean;
    unlottery?: boolean;
    universities?: string[];
    graduate_years?: string[];
    grades?: string[];
    selection_status?: string[];
    event_status?: string[];
    schedule_ids?: number[];
    entry_ids?: number[];
    entry_date_from?: string;
    entry_date_to?: string;
    order: Order;
    limit?: number;
    offset?: number;
  }

  export interface EventEntryUsersGenerateCsv extends EventEntryUsers {
    output_columns: string[];
  }

  export interface EventEntryUsersSearchOptions {
    eventId: string;
  }

  export interface updateEventEntryUserStar {
    entryId: number;
  }

  export interface deleteEventEntryUserStar {
    entryId: number;
  }

  export interface UpdateEventStatus {
    entry_ids: number[];
    event_status: string;
  }

  export interface UpsertPartnerEntryCsvColumns {
    csv_columns: { column: string; position: number }[];
  }

  export interface CreateAgreement {
    partner_user_id: number;
    service_term_id: number;
  }

  export interface GetPerformanceEvent {
    event_id: number | null;
    start_date: string;
    end_date: string;
  }

  export interface CreatePerformanceEventCsv {
    event_id: number;
    start_date: string;
    end_date: string;
  }

  export interface GetPerformanceOfficial {
    start_date: string;
    end_date: string;
  }

  export interface CreatePerformanceOfficialCsv {
    start_date: string;
    end_date: string;
  }

  export interface GetPerformanceScout {
    template_id: number | null;
    start_date: string;
    end_date: string;
  }

  export interface CreatePerformanceScoutCsv {
    template_id: number;
    start_date: string;
    end_date: string;
  }

  export interface AgentScoutAcceptancesBase {
    starred?: boolean;
    universities: string[];
    graduate_years: string[];
    grades: string[];
    keyword: string;
    accepted_date_from?: string;
    accepted_date_to?: string;
    order: string;
  }

  export interface AgentScoutAcceptances extends AgentScoutAcceptancesBase {
    limit: number;
    offset: number;
  }

  export interface AgentScoutAcceptancesGenerateCsv
    extends AgentScoutAcceptancesBase {
    output_columns: string[];
  }

  export interface AgentScoutAcceptancesOrder {
    order: Order;
  }
}

export type UniversityCourse = "HUMANITIES" | "SCIENCE" | "UNSPECIFIED";

export namespace ApiResponse {
  export interface ZendeskJwt {
    sso_payload: string | null;
  }

  export interface Contract {
    id: number;
    plan: string;
    graduate_year: string;
    start_at: string;
    end_at: string;
    policies: {
      resource_name: string;
      level: number;
    }[];
  }

  export interface GetContracts {
    available_contracts: Contract[];
    expired_contracts: Contract[];
  }

  export interface Partner {
    id: number;
    exist_credit: boolean;
    company: Company;
    recruitment_number_registered: boolean;
    contracts: Contract[];
  }

  export interface PartnerFile {
    id: number;
    file_name: string;
    file_size: number;
    downloaded: boolean;
    last_downloaded_partner_user_name: string;
    uploaded_at: string;
  }

  export interface PartnerFiles {
    total: number;
    partner_files: PartnerFile[];
  }

  export interface GetPartnerFile {
    url: string;
  }

  export interface GetPartnerUser {
    email: string;
    name: string;
    partner: Partner;
    onboarding_finished: boolean;
    privileges?: string[];
  }

  export interface GetPartnerUserWithAdmin extends GetPartnerUser {
    admin: boolean;
  }

  export interface PartnerUser extends GetPartnerUser {
    id: number;
    authentication_token?: string;
    logged_in_by_proxy?: boolean;
  }

  export interface SignIn {
    partner_user: PartnerUser;
  }

  export interface SignOut {}

  export interface PasswordReissue {}

  export interface PasswordReset {
    partner_user: PartnerUser;
  }

  export type AvailableGraduateYears = number[];

  export interface Plan {
    id: number;
    name: string;
    graduate_year: number;
    created_at: string;
    updated_at: string;
  }

  export interface PlanUpdate {
    partner_id: number;
    name: string;
    id: number;
    graduate_year: number;
    created_at: string;
    updated_at: string;
  }

  export interface EventCreate {}

  export interface EventUpdate {}

  export interface EventDelete {}

  export interface PlanDetail {
    id: number;
    name: string;
    graduate_year: number;
    created_at: string;
    updated_at: string;
    recruitment_events: RecruitmentEvent[];
    competitors: Competitor[];
    student_segments: StudentSegment[];
  }

  export interface Companies {
    total: Application.CompaniesTotal;
    companies: CompanyAll[];
  }

  export interface CompactUniversities {
    universities: Pick<ApiResponse.University, "id" | "name">[];
  }

  export interface Universities {
    universities: University[];
  }

  export interface CompetitorEmbedded extends Competitor {
    events: CompetitorEvent[];
  }

  export interface StudentEmbedded extends StudentSegment {
    events: StudentEvent[];
  }

  export interface Competitor {
    id: number;
    company: Company;
    created_at: string;
    updated_at: string;
  }

  export interface StudentSegment {
    id: number;
    university: University;
  }

  export interface StudentJobHuntingTrendCollection {
    university_id: number;
    datasets: StudentJobHuntingTrendDataset[];
  }

  export interface EventExperiences {
    competitor: {
      company: Company;
    };
    graduate_year: string;
    event_experiences: EventExperience[];
  }

  export interface EventExperienceEmbedded {
    competitor: {
      company: Company;
    };
    graduate_year: string;
    event_experience: EventExperience;
  }

  export interface NewEventExperienceEmbedded extends EventExperienceEmbedded {
    category?: CompanyDetailReviewCategory;
    experience_review?: ExperienceReview;
  }

  export interface ExperienceReview {
    rating_ave: number;
    rating1: number;
    rating2: number;
    rating3: number;
    rating4: number;
    rating5: number;
    rating6: number;
    detail1: string;
    detail2: string;
    detail3: string;
    detail4: string;
    detail5: string;
    detail6: string;
  }

  export interface EventDetailEmbedded {
    competitor: {
      company: Company;
    };
    event_detail: EventDetail;
  }

  export type Reports = {
    year: number;
    month: number;
    week: number;
    url: string;
  }[];

  export interface Report {
    graduate_year: number;
    company: {
      id: number;
      name: string;
      rating: number;
    };
    reports: NestedReport[];
  }

  export type Notifications = Notification[];

  export interface NotificationSetting {
    add_competitor_intern: boolean; // 企業の「新規インターン公開」
    update_competitor_intern: boolean; // 企業の「インターン情報変更」
    add_competitor_experience: boolean; // 企業の「新規体験談公開」
    add_target_university_schedule: boolean; // 大学の「スケジュール追加」
    update_target_university_schedule: boolean; // 大学の「スケジュール変更」
  }

  export interface Clip {
    id: number;
    category: ClipCategory;
    experience: EventExperienceEmbedded | null;
    event_detail: EventDetailEmbedded | null;
  }

  export interface FollowCompanies {
    companies: CompanyWithFollow[];
  }

  export interface FollowsUniversities {
    universities: Pick<University, "id" | "name">[];
  }

  // NOTE: 以下ApiResponseはapi内では未使用

  export interface ErrorData {
    name: string;
    messages: string[];
    // error_codeはi-web連携設定エラーの場合のみ返却される
    error_code?: string;
    disagreed?: boolean;
  }

  export interface ErrorResponse {
    data: {
      errors: ErrorData[];
    };
    status: number;
    statusText: string;
    headers: object;
    config: AxiosRequestConfig;
    request: XMLHttpRequest;
  }

  export interface ApiError extends Error {
    response: ErrorResponse;
  }

  export interface Company {
    id: number;
    name: string;
    rating: number;
    square_logo_url: string;
  }

  export interface CompanyWithLiked extends Company {
    liked_count: number;
  }

  export interface CompanyWithLikeCount extends Company {
    like_count: number;
    like_count_by_university?: number;
    report_count?: number;
  }

  export interface CompanyWithRank extends CompanyWithLikeCount {
    rank: number;
    previous_rank: number;
  }

  export interface CompanyWithFollow extends CompanyWithLikeCount {
    followed: false;
    description: string;
  }
  export interface NewExperience {
    label: InHouseOtherCompetitors;
    rating_ave: number;
    text: string;
    category: EventPurpose;
    company: Pick<Company, "id" | "name">;
  }

  export interface UniversityStudentsPopularCompanies {
    id: number;
    name: string;
    count: number;
  }

  export interface CompaniesExplorer {
    total: number;
    university_names?: string[];
    company_name: string;
    business_category_name: string;
    companies: CompanyWithRank[];
  }

  export interface CompaniesExplorerRank {
    total: number;
    university_names?: string[];
    business_category_name: string;
    companies: CompanyWithRank[];
  }

  export interface CompanyDetail extends ApiResponse.Company {
    description: string;
    business_category: string;
    affiliated: string;
    award_badges: AwardReview[];
    user_reports: UserReports[];
    reviews: ReviewGraph[];
    favorites: FavoriteGraph[];
    followed: boolean;
  }

  export type CompanyDetailV2 = CompanyDetail & {
    business_category: {
      id: number;
      name: string;
    };
  };

  export interface BusinessCategoriesAverages
    extends Pick<CompanyDetail, "reviews" | "favorites"> {
    id: number;
    name: string;
  }

  export interface AwardReview {
    badge: AwardBadge;
    year: number;
  }

  export interface ReviewGraph {
    category: CompanyDetailReviewCategory;
    graduate_year: number;
    review_count: number;
    rating1?: number;
    rating2?: number;
    rating3?: number;
    rating4?: number;
    rating5?: number;
    rating6?: number;
  }

  export interface FavoriteGraph {
    graduate_year: number;
    graph_items: {
      year: number;
      month: number;
      favorites_count: number;
    }[];
  }

  export interface CompanyDetailRatings {
    ratings: CompanyDetailRating[];
  }

  export interface CompanyDetailReviewData {
    id: number;
    review: {
      [key: string]: number | string;
      rating_ave: number;
      rating1: number;
      rating2: number;
      rating3: number;
      rating4: number;
      rating5: number;
      rating6: number;
      detail1: string;
      detail2: string;
      detail3: string;
      detail4: string;
      detail5: string;
      detail6: string;
    };
    info: {
      graduate_year: number;
      occupation?: string;
      season?: string;
      schedule?: string;
      location?: string;
      period?: string;
      format?: string;
      result?: string;
      offer_season?: string;
      contract?: string;
    };
  }

  export interface CompanyAll extends Company {
    description: string;
    published: boolean;
    receive_published: boolean;
  }

  export interface RecruitmentEvent {
    id: number;
    name: string;
    event_type: string;
    start_at: string;
    end_at: string;
    description: string;
    reference_url: string;
    created_at: string;
    updated_at: string;
  }

  export interface CompetitorEvent {
    name: string;
    graduate_year: number;
    category: EventCategory;
    purpose: EventPurpose;
    start_at: BMEPeriodForCompetitor;
    end_at: BMEPeriodForCompetitor;
    competitor_event_ids: number[] | null;
    event_detail_id: number | null;
  }

  export interface StudentEvent {
    name: string;
    start_at: BMEPeriodForStudent;
    end_at: BMEPeriodForStudent;
    reference_url?: string;
  }

  export interface University {
    id: number;
    name: string;
    course: UniversityCourse;
  }

  export interface Students {
    id: number;
    university: University;
    events: StudentEvent[];
  }

  export type BMEPeriodBase = {
    type: DATE_TYPE;
    year: number;
    month: number;
    part: BME_PART;
  };

  export interface BMEPeriodForCompetitor extends BMEPeriodBase {
    day: number | null;
  }

  export interface BMEPeriodForStudent extends BMEPeriodBase {
    month: number;
    day: number;
  }

  export type GraphCategory =
    | "MEMBER_REGISTRATION_COUNT"
    | "EVENT_ENTRY_COUNT"
    | "OFFER_RESULT_COUNT";

  export interface StudentJobHuntingTrendData {
    graduate_year: number;
    year: number;
    month: number;
    part: BME_PART;
    count: number;
  }

  export interface StudentJobHuntingTrendDataset {
    category: GraphCategory;
    data: StudentJobHuntingTrendData[];
  }

  export interface EventExperience {
    id: number;
    clip_id: number | null;
    name: string;
    job_category: string;
    period: string;
    experience_contents: {
      table_contents: {
        label: string;
        value: string;
      }[];
      text_contents: {
        label: string;
        value: string;
      }[];
    };
    selection_report: {
      id: number;
      name: string;
      // NOTE: 説明会には存在しない
      selection_report_items?: {
        id: number;
        category: string;
        item_type: string;
      }[];
    };
  }

  export interface EventDetail {
    id: number;
    clip_id: number;
    graduate_year: number;
    name: string;
    overview: string;
    screening_process: string;
    schedule: string;
    period: string;
    location: string;
    number_of_participants: string;
    deadline: string;
    reward: string;
    treatment: string;
    target: string;
    reference_url: string;
  }

  export type LikedReport = {
    increments: number;
    total: number;
    detail: {
      male_count: number;
      female_count: number;
      bachelor_count: number;
      master_count: number;
      humanities_course_count: number;
      science_course_count: number;
      increments_of_universities: {
        university: {
          id: number;
          name: string;
        };
        count: number;
      }[];
      total_of_universities: {
        university: {
          id: number;
          name: string;
        };
        count: number;
      }[];
    };
  };

  export type NestedReport = {
    id: number;
    report_type: ReportType;
    start_date: string;
    end_date: string;
    year: number;
    month: number;
    week: number;
    liked_report: LikedReport;
  };

  export enum NOTIFICATION_LARGE_CATEGORIES {
    COMPETITOR = "competitor",
    UNIVERSITY = "university"
  }

  export type NotificationLargeCategory =
    | NOTIFICATION_LARGE_CATEGORIES.COMPETITOR
    | NOTIFICATION_LARGE_CATEGORIES.UNIVERSITY;

  export enum NOTIFICATION_CATEGORIES {
    NEW_EVENT = "new_event",
    EXPERIENCE = "experience",
    SEARCH = "search",
    UPDATE_EVENT = "update_event",
    BULK_INSERT = "bulk_insert",
    PARTNER_NOTIFICATION_NEW = "partner_notification_new",
    PARTNER_NOTIFICATION_CHANGE = "partner_notification_change",
    PARTNER_NOTIFICATION_CONFIRM = "partner_notification_confirm"
  }

  export type NotificationCategory =
    | NOTIFICATION_CATEGORIES.NEW_EVENT
    | NOTIFICATION_CATEGORIES.EXPERIENCE
    | NOTIFICATION_CATEGORIES.SEARCH
    | NOTIFICATION_CATEGORIES.UPDATE_EVENT
    | NOTIFICATION_CATEGORIES.BULK_INSERT
    | NOTIFICATION_CATEGORIES.PARTNER_NOTIFICATION_NEW
    | NOTIFICATION_CATEGORIES.PARTNER_NOTIFICATION_CHANGE
    | NOTIFICATION_CATEGORIES.PARTNER_NOTIFICATION_CONFIRM;

  export interface Notification {
    id: number;
    large_category: NotificationLargeCategory;
    category: NotificationCategory;
    datetime: string;
    title: string;
    text: string | null;
    notification_image_url: string;
    url: string;
    read_at: string | null;
  }

  export interface ContentOffersItem {
    id: number;
    job_category_name: string;
    type_name: string;
  }

  export interface ContentOffersByYear {
    content_offers: {
      graduate_year: number;
      content_offer: ContentOffersItem[];
    }[];
  }

  export interface ContentOffer {
    mail_body: string;
    content_offer_url: string;
  }

  export interface ContentOfferMailBody {
    mail_body: string;
  }

  export enum CLIP_CATEGORIES {
    EXPERIENCE = "experience",
    EVENT_DETAIL = "event_detail"
  }

  export type ClipCategory =
    | CLIP_CATEGORIES.EXPERIENCE
    | CLIP_CATEGORIES.EVENT_DETAIL;

  export interface TopCompanyFollowees {
    companies: CompanyWithFollow[];
  }

  export interface TopUniversityFollowees
    extends ApiResponse.CompactUniversities {}

  export interface JobEventListItem {
    id: number;
    title: string;
    eye_catch_image?: string;
    approval_status: JobEventApprovalStatus;
    public_status: JobEventPublicStatus;
    graduate_year: string;
    entry_count: number;
    unlottery_count?: number;
    publish_at?: string;
    updated_at: string;
    is_pre_entry_event: boolean;
  }

  export interface JobEventList {
    total: number;
    events: JobEventListItem[];
  }

  export interface JobEvent {
    draft_event: Pick<
      JobEventListItem,
      | "title"
      | "eye_catch_image"
      | "approval_status"
      | "public_status"
      | "publish_at"
      | "updated_at"
    > & {
      id?: number;
      event_id: number;
      event_description_items: JobEventDescription[];
      approved_at: string;
      preferred_publish_at: string | null;
      preferred_updated_at: string; // NOTE: preferred_publish_atに一本化されたため廃止
      scheduled_publish: boolean;
      reject_reason?: string;
      contract_id: number;
      category: JobEventCategory;
      reception_type: JobEventReceptionType;
      application_url: string;
      is_lottery: boolean;
      university_names: {
        university_name: string;
      }[];
      iweb_code?: string;
      iweb_type?: IwebType;
      iweb_graduate_year?: string;
      comment?: string;
      mail_notifications: JobEventMailNotification;
      schedules: JobEventSchedule[];
      resume_questions: JobEventResume[];
    };
    comments: JobEventComment[];
  }

  export interface JobEventSchedules {
    schedules: JobEventSchedule[];
    draft_schedules: JobEventDraftSchedule[];
  }

  export interface PostJobEvent {
    event_id: number;
    draft_event_id: number;
  }

  export interface PostJobEvent
    extends Omit<
      ApiRequest.JobEvent["draft_event"],
      "publish_at" | "updated_at" | "approved_at"
    > {}

  export interface JobEventMailTemplateVariable {
    variables: {
      name:
        | "user_name"
        | "graduate_year"
        | "university_name"
        | "event_title"
        | "event_url"
        | "schedule_title"
        | "schedule_date"
        | "schedule_info"
        | "cancel_remarks"
        | "resume_remarks";
      description: string;
    }[];
  }
  export interface UpdateJobEventDraftEntryMailTemplates {
    entry_mail_template: JobEventMailType;
  }
  export interface GetJobEventEntryMailTemplates {
    entry_mail_template: JobEventMailType & JobEventMailUpdatedAtType;
  }
  export interface ComingEventItem {
    id: number;
    event_title: string;
    schedule_id: number;
    schedule_title: string;
    schedule_start_at: string;
    schedule_place: string;
    today_entry_count: number;
    total_entry_count: number;
    today_cancel_count: number;
    total_cancel_count: number;
    unlottery_count: number;
    is_lottery: boolean;
  }
  export interface GetComingEvents {
    events: ComingEventItem[];
  }
  export interface UpdateJobEventEntryMailTemplates {
    entry_mail_template: JobEventMailType;
  }
  export interface UpdateJobEventMailNotificationTypes {
    event: JobEvent;
    draft_event: Overwrite<
      ApiResponse.JobEvent["draft_event"],
      {
        event_description_items: PartiallyPartial<JobEventDescription, "id">[];
        schedules: PartiallyPartial<JobEventSchedule, "id">[];
        resume_questions: PartiallyPartial<JobEventResume, "id">[];
      }
    >;
  }
  export interface EventGraduateYears {
    graduate_years: string[]; // NOTE: "2023年卒"
  }
  export interface SponsoredEventItem {
    id: number;
    title: string;
    eye_catch_image: string;
    graduate_year: string[];
    use_checkin: boolean;
    checkin_count: number;
    entry_count: number;
    updated_at: string;
  }

  export interface SponsoredEventList {
    total: number;
    sponsored_events: SponsoredEventItem[];
  }

  // TODO: 卒年取得のAPIが複数あるため整理・統一する
  export interface SponsoredAvailableGraduateYears {
    graduate_years: string[];
  }

  export interface SponsoredEventEntryCheckinList {
    entry_checkins: SponsoredEventEntryCheckin[];
    total: number;
  }

  export interface SponsoredEventEntryCheckin {
    id: number;
    star: boolean;
    created_at: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      first_name_kana: string;
      last_name_kana: string;
      university_name: string;
      department_name: string;
      grade: string;
      graduate_year: string;
    };
    schedule: {
      id: number;
      title: string;
      date: {
        start: string;
        end: string;
      };
    };
  }

  export interface SponsoredEvent {
    title: string;
    use_checkin: boolean;
  }

  export interface SponsoredEventEntryCheckinSearchOptions {
    universities: string[];
    graduate_years: string[];
    grades: string[];
    events: PartnerEntryUserEventSelectItem[];
    schedules: PartnerEntryUserScheduleSelectItem[];
  }

  // 申込者一覧
  export interface SponsoredEventEntryApplicantList {
    applicants: SponsoredEventEntryApplicant[];
    total: number;
  }

  export interface SponsoredEventEntryApplicant {
    id: number;
    star: boolean;
    created_at: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      first_name_kana: string;
      last_name_kana: string;
      university_name: string;
      department_name: string;
      grade: string;
      graduate_year: string;
    };
    schedule: {
      id: number;
      title: string;
      date: {
        start: string;
        end: string;
      };
    };
  }

  export interface SponsoredEventEntryApplicantEventSelectItem {
    id: number;
    title: string;
  }

  export interface SponsoredEventEntryApplicantScheduleSelectItem {
    id: number;
    title: string;
  }

  export interface SponsoredEventEntryApplicantSearchOptions {
    universities: string[];
    graduate_years: string[];
    grades: string[];
    events: SponsoredEventEntryApplicantEventSelectItem[];
    schedules: SponsoredEventEntryApplicantScheduleSelectItem[];
  }

  export interface PartnerEntryUser {
    partner_entry_user: {
      id: number;
      user_id: number;
      first_name: string;
      last_name: string;
      first_name_kana: string;
      last_name_kana: string;
      unique_id: string;
      university_name: string;
      department_name: string;
      grade: string;
      graduate_year: string;
    };
    possible_transitions: PartnerEntryUserSelectionStatus[];
    selection_status_name: PartnerEntryUserSelectionStatus;
    entry_date: string;
    entry_route: string;
    event: {
      type: string;
      lottery: boolean;
      title: string;
      status_name: string;
      schedule: {
        title: string;
        date: {
          start: string;
          end: string;
        };
      };
    };
    star: boolean;
    created_at: string;
    updated_at: string;
  }

  export interface BulkEditPartnerEntryUser extends PartnerEntryUser {
    changable: boolean;
    changable_errors: string[];
  }

  export interface PartnerEntryUserList {
    total: number;
    records: PartnerEntryUser[];
  }

  export interface PartnerEntryUserDetail {
    partner_entry_user_id: number;
    basic_info: {
      name: string;
      name_kana: string;
      unique_id: string;
      status: PartnerEntryUserSelectionStatus;
      date_of_birth: string;
      sex: string;
      full_address: string;
      tel: string;
      email: string;
      private_email: string;
    };
    belongs: PartnerEntryUserBelongs;
    educational_backgrounds: {
      school_name: string;
      start_on: string;
      end_on: string;
      status: string;
    }[];
    activities: {
      self_promotion_and_student_endeavor: PartnerEntryUserSelfPromotionAndStudentEndeavor;
      internships: PartnerEntryUserInternship[];
      unofficial_offers: PartnerEntryUserUnofficialOffer[];
      activities: PartnerEntryUserActivity[];
      languages: PartnerEntryUserLanguage[];
      programming_skills: PartnerEntryUserProgrammingSkill[];
      qualifications: PartnerEntryUserQualification[];
      hobby: PartnerEntryUserHobby;
    };
  }

  export interface VideoBundleUnlockingLog {
    user: {
      last_name: string;
      first_name: string;
      last_name_kana: string;
      first_name_kana: string;
      unique_id: string;
      graduate_year: string;
      university_name: string;
      department_name: string;
      grade: string;
    };
    video_bundle_unlocking_log: {
      unlocked_at: string;
      video_bundle_title: string;
      video_chapter_title: string;
    };
  }

  export interface VideoBundleUnlockingLogsSearchOptions {
    universities: string[];
    graduate_years: string[];
    grades: string[];
    video_bundle_titles: string[];
    video_chapter_titles: string[];
  }

  export interface VideoBundleUnlockingLogsList {
    total: number;
    records: VideoBundleUnlockingLog[];
  }

  export interface GetScheduleOptions {
    schedules_info: {
      id: number;
      title: string;
      applying: boolean;
    }[];
  }

  export interface PartnerEntryUserBelongs {
    university_name: string;
    department_name: string;
    concentration_name: string;
    grade: string;
    graduate_year: string;
    seminar_circle: string;
    course: string;
    high_school: string;
  }

  export interface PartnerEntryUserSelfPromotionAndStudentEndeavor {
    self_promotion_content: string | null;
    student_endeavor_content: string | null;
  }

  export interface PartnerEntryUserInternship {
    company_name: string;
    course: string;
    job_category: string;
    start_on: string;
    end_on: string;
    status: PartnerEntryUserInternshipStatus;
  }

  export interface PartnerEntryUserUnofficialOffer {
    company_name: string;
    job_category: string;
    called_on: string;
    status: PartnerEntryUserUnofficialOfferStatus;
  }

  export interface PartnerEntryUserLanguage {
    name: string;
    level: string;
  }

  export interface PartnerEntryUserProgrammingSkill {
    name: string;
    year_of_use: string;
    description: string;
  }

  export interface PartnerEntryUserQualification {
    title: string;
    get_on: string;
  }

  export interface PartnerEntryUserActivity {
    large_category: string;
    medium_category: string;
    description: string;
  }

  export interface PartnerEntryUserHobby {
    description: string;
  }

  export interface PartnerEntryUserEvents {
    entries: PartnerEntryUserEvent[];
  }

  export interface PartnerEntryUserEvent {
    id: number;
    event_id: number;
    entried_at: string;
    event_title: string;
    event_lottery: boolean;
    schedule_title: string;
    schedule_start_on: string;
    schedule_end_on: string;
    resume_questions: string[];
    state: PartnerEntryUserEventStatus;
    state_possible_transitions: PartnerEntryUserEventStatus[];
  }

  export interface UserScoutEntry {
    scout_entry: {
      message_thread_id: number;
      user_accepted_at: string;
    };
  }

  export interface PartnerEntryUserMemos {
    partner_entry_user_memos: {
      id: number;
      partner_entry_user_id: number;
      content: string;
      created_at: string;
      updated_at: string;
    }[];
  }

  export interface PartnerEntryUserResumeAnswers {
    resume_answers: {
      title: string;
      content: string;
    }[];
  }

  export interface BulkEditPartnerEntryUserList {
    total: number;
    records: BulkEditPartnerEntryUser[];
  }

  export interface PartnerEntryUserEventSelectItem {
    id: number;
    title: string;
  }

  export interface PartnerEntryUserScheduleSelectItem {
    id: number;
    title: string;
  }

  export interface PartnerEntryUserSearchOptions {
    universities: string[];
    graduate_years: string[];
    grades: string[];
    selection_statuses: string[];
    event_statuses: string[];
    events: PartnerEntryUserEventSelectItem[];
    event_schedules: {
      [key: number]: { schedules: PartnerEntryUserScheduleSelectItem[] };
    };
  }

  export interface ManagedPartnerUser {
    id: number;
    name: string;
    email: string;
    admin: boolean;
  }

  export interface RecruitmentNotificationSetting {
    daily_report: boolean; // 求人関連の「デイリーレポート」
    schedule_changed: boolean; // 求人関連の「イベント参加日程の変更通知」
    todays_events: boolean; // 求人関連の「イベント当日の応募状況レポート」
    new_entries: boolean; // 求人関連の「新着応募通知」
    file_uploaded: boolean; // 求人関連の「ファイルアップロード通知」
    scout_accepted: boolean; // スカウト受諾通知
    scout_declined: boolean; // スカウト却下通知
    scout_replyed: boolean; // スカウト返信通知
    scout_weekly_report: boolean; // スカウトウィークリーレポート
    scout_monthly_report: boolean; // スカウトマンスリーレポート
  }

  export interface GetAccountsFollowCompanies {
    companies: Company[];
  }

  export interface GetAccountsPopularCompanies {
    companies: Company[];
  }

  export interface FollowUniversity {
    id: number;
    name: string;
  }

  export interface GetAccountsFollowUniversities {
    universities: FollowUniversity[];
  }

  export interface GetAccountsPopularUniversities {
    universities: FollowUniversity[];
  }

  export interface ContractInformation {
    id: number;
    graduate_year: string;
    plan: string;
    period: string;
  }

  export interface ContractInformations {
    contract_informations: ContractInformation[];
  }

  export interface GetAccountsIntegrationSonarToken {
    sonar_token: string;
  }

  export interface ScoutIwebIntegration {
    contract_graduate_year_id: number;
    display_contract_name: string;
    contract_graduate_year: string;
    contract_type: string;
    iweb_integration_code: string;
    iweb_integration_type: string;
    iweb_integration_graduate_year: string;
  }

  export interface GetScoutIwebIntegrations {
    scout_iweb_integrations: ScoutIwebIntegration[];
  }

  export interface EventIwebIntegration {
    event_id: number;
    recruitment_title: string;
    event_graduate_year: string;
    event_category: string;
    iweb_integration_code: string;
    iweb_integration_type: string;
    iweb_integration_graduate_year: string;
  }

  export interface GetEventIwebIntegrations {
    event_iweb_integrations: EventIwebIntegration[];
  }

  export interface GetIwebIntegrationSetting {
    graduate_years: string[];
    integration_types: { [key: string]: string };
  }

  export interface PartnerUsersPrivilege {
    id: number;
    name: string;
    privileges: string[];
  }

  export type PartnerUserPrivilegeList = PartnerUsersPrivilege[];

  export interface PartnerUsersPrivileges {
    partner_users: PartnerUserPrivilegeList;
  }

  export interface Announcement {
    title: string;
    published_date: string;
    content: string;
    announcement_type: AnnouncementType;
    announcement_method: AnnouncementMethod;
    service_term: {
      id?: number;
      terms_type?: TermsType;
    };
    require_agreement: boolean;
    updated_at: string;
  }

  export type Announcements = Announcement[];

  export interface GetCompanyLogo {
    id: number;
    name: string;
    square_logo_url: string;
  }

  export interface CompanyRank {
    total: number;
    rank: number;
    previous_rank: number;
  }

  export interface Official {
    id: number;
    contract_id: number;
    public_status: OfficialPublicStatus;
    approval_status: OfficialApprovalStatus;
    version: string;
  }

  export interface OfficialData {
    id: number;
    official_id: number;
    label: string;
    text: string;
    sort_order: number;
    created_at: string;
    updated_at: string;
  }

  export interface EventEntryUser {
    id: number;
    event_entry_user: {
      id: number;
      first_name: string;
      last_name: string;
      first_name_kana: string;
      last_name_kana: string;
      unique_id: string;
      university_name: string;
      department_name: string;
      grade: string;
      graduate_year: string;
    };
    entry_date: string;
    entry_route: string;
    event: {
      type: string;
      lottery: boolean;
      title: string;
      status_name: string;
      possible_transitions: string[];
      schedule: {
        title: string;
        date: {
          start: string;
          end: string;
        };
      };
    };
    star: boolean;
    created_at: string;
    updated_at: string;
  }

  export interface OfficialContent {
    id: number;
    official_id: number;
    title: string;
    content_type: OfficialContentType;
    position: number;
    image1_url: string;
    image2_url: string;
    image3_url: string;
    image4_url: string;
    description1: string;
    description2: string;
    description3: string;
    description4: string;
    movie1: string;
    movie2: string;
    movie3: string;
    movie4: string;
    created_at: string;
    updated_at: string;
  }

  export interface OfficialDetail {
    id: number;
    company_id: number;
    created_at: string;
    updated_at: string;
    confirmed_at: string;
    publish_at: string;
    start_at: string;
    submitted_at: string;
    official_image_url: string;
    top_head_image_url: string;
    official_description: string;
    reject_reasons: string[];
    reject_reason: string;
    contract_id: number;
    public_status: OfficialPublicStatus;
    approval_status: OfficialApprovalStatus;
    preview_url: string;
    official_data: OfficialData[];
    official_contents: OfficialContent[];
  }

  export interface Officials {
    officials: Official[];
    total: number;
  }

  export interface EventEntryUserList {
    total: number;
    event: {
      id: number;
      type: JobEventCategory;
      lottery: boolean;
      title: string;
    };
    records: EventEntryUser[];
  }

  export interface EventEntryUserSearchOptions {
    universities: string[];
    graduate_years: string[];
    grades: string[];
    event_statuses: string[];
    schedules: EventEntryUserScheduleSelectItem[];
  }

  export interface EventEntryUserScheduleSelectItem {
    id: number;
    title: string;
  }

  export interface PartnerEntryCsvColumn {
    id: number;
    column: string;
    position: number;
  }

  export interface PartnerEntryCsvColumns {
    csv_columns: PartnerEntryCsvColumn[];
  }

  export interface PerformanceEvents {
    event_id: number;
    business_category_name: string;
    events: EventItem[];
    total_entry_count: number;
    total_pv: number;
    event_reports: EventReportItem[];
    category_reports: CategoryReportItem[];
  }

  export interface EventItem {
    event_id: number;
    title: string;
    grade: string;
  }

  export interface EventReportItem {
    date: string;
    total_pv: number;
    total_entry_count: number;
    kdd_entry_count: number;
    wkuik_entry_count: number;
    gmarch_entry_count: number;
    kkdr_entry_count: number;
    ntks_entry_count: number;
    skkl_entry_count: number;
    national_entry_count: number;
    other_entry_count: number;
  }

  export interface CategoryReportItem {
    date: string;
    average_event_show_pv: number;
    average_entry_count: number;
  }

  export interface UniversityReportItem {
    university_group: string;
    total_entry_count: number;
  }

  export interface PerformanceOfficial {
    business_category_name: string;
    total_vote_count: number;
    total_pv: number;
    official_reports: OfficialReportItem[];
    category_reports: OfficialCategoryReportItem[];
  }

  export interface OfficialReportItem {
    date: string;
    total_pv: number;
    total_vote_count: number;
    kdd_vote_count: number;
    wkuik_vote_count: number;
    gmarch_vote_count: number;
    kkdr_vote_count: number;
    ntks_vote_count: number;
    skkl_vote_count: number;
    national_vote_count: number;
    other_vote_count: number;
  }

  export interface OfficialCategoryReportItem {
    date: string;
    average_company_pv: number;
    average_votes_count: number;
  }

  export interface PerformanceScouts {
    business_category_name: string;
    category_reports: ScoutCategoryReportItem[];
    scout_reports: ScoutReportItem[];
    template_id: number;
    templates: TemplateItem[];
    total_accepted_count: number;
    total_send_count: number;
  }

  export interface TemplateItem {
    template_id: number;
    title: string;
  }

  export interface ScoutReportItem {
    calculation_date: string;
    total_send_count: number;
    total_accepted_count: number;
    kdd_accepted_count: number;
    wkuik_accepted_count: number;
    gmarch_accepted_count: number;
    kkdr_accepted_count: number;
    ntks_accepted_count: number;
    skkl_accepted_count: number;
    national_accepted_count: number;
    other_accepted_count: number;
  }

  export interface ScoutCategoryReportItem {
    calculation_date: string;
    average_send_count: number;
    average_accepted_count: number;
  }

  export interface AgentScoutAcceptanceList {
    agent_scout_acceptances: AgentScoutAcceptance[];
    total: number;
  }

  export interface AgentScoutAcceptance {
    id: number;
    star: boolean;
    created_at: string;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      first_name_kana: string;
      last_name_kana: string;
      university_name: string;
      department_name: string;
      grade: string;
      graduate_year: string;
    };
  }

  export interface AgentScoutAcceptanceSearchOptions {
    universities: string[];
    graduate_years: string[];
    grades: string[];
  }
}
